import { lazy, type ComponentType } from 'react'

const RELOAD_KEY = 'chunk-reload'

/**
 * Wraps a dynamic-import factory so that a chunk-load failure (typically a stale
 * hashed asset that no longer exists on the CDN after a frontend deploy) forces a
 * one-time full page reload. The reload fetches a fresh index.html with the new
 * chunk hashes, self-healing the "Failed to fetch dynamically imported module" error.
 *
 * A sessionStorage flag prevents an infinite reload loop if the asset is genuinely
 * gone; the flag is cleared on the first successful load so future deploys self-heal.
 *
 * The import can also *resolve* to `undefined`: when `initPreloadErrorHandler`
 * cancels a `vite:preloadError` event to reload the page itself, Vite's preload
 * helper swallows the failure and hands back nothing. That is a reload already
 * in flight, so we halt the same way rather than let callers read `m.Login`
 * off `undefined` (Sentry APP-E).
 */
export function withReload<T>(factory: () => Promise<T>): () => Promise<T> {
  return () =>
    factory().then(
      (mod) => {
        if (mod === undefined) return new Promise<T>(() => undefined)
        sessionStorage.removeItem(RELOAD_KEY)
        return mod
      },
      (err: unknown) => {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, '1')
          window.location.reload()
          // Never resolve — halt rendering while the page reloads.
          return new Promise<T>(() => undefined)
        }
        throw err // already retried — surface to the ErrorBoundary
      },
    )
}

interface RemoteConfig {
  loader: () => Promise<{ default: ComponentType }>
}

const REMOTES: Record<string, RemoteConfig> = {
  org: {
    loader: withReload(() => import('../../org/src/remote.tsx')),
  },
  admin: {
    loader: withReload(() => import('../../admin/src/remote.tsx')),
  },
}

const remoteCache = new Map<string, ReturnType<typeof lazy>>()

export function getRemote(name: string) {
  const cached = remoteCache.get(name)
  if (cached) return cached

  const config = REMOTES[name]
  if (!config) throw new Error(`Unknown remote: ${name}`)

  const LazyComponent = lazy(config.loader)
  remoteCache.set(name, LazyComponent)
  return LazyComponent
}

// Identity pages — exported as named components, lazy-loaded individually
const identityRemote = withReload(() => import('../../identity/src/remote.tsx'))

export const IdentityLogin = lazy(() => identityRemote().then(m => ({ default: m.Login })))
export const IdentitySignup = lazy(() => identityRemote().then(m => ({ default: m.Signup })))
export const IdentityWelcome = lazy(() => identityRemote().then(m => ({ default: m.Welcome })))
export const IdentityLinkDone = lazy(() => identityRemote().then(m => ({ default: m.LinkDone })))
export const IdentityProfileSettings = lazy(() => identityRemote().then(m => ({ default: m.ProfileSettings })))
export const IdentitySettingsConnections = lazy(() => identityRemote().then(m => ({ default: m.SettingsConnections })))
export const IdentityLegacyConnectionsRedirect = lazy(() => identityRemote().then(m => ({ default: m.LegacyConnectionsRedirect })))
export const IdentitySettingsPhone = lazy(() => identityRemote().then(m => ({ default: m.SettingsPhone })))
export const IdentitySettingsTeamContact = lazy(() => identityRemote().then(m => ({ default: m.SettingsTeamContact })))
export const IdentitySettingsSecurity = lazy(() => identityRemote().then(m => ({ default: m.SettingsSecurity })))
export const IdentitySettingsAccount = lazy(() => identityRemote().then(m => ({ default: m.SettingsAccount })))
export const IdentitySettingsSignIn = lazy(() => identityRemote().then(m => ({ default: m.SettingsSignIn })))
export const IdentitySettingsCalendars = lazy(() => identityRemote().then(m => ({ default: m.SettingsCalendars })))
export const IdentityProfileLayout = lazy(() => identityRemote().then(m => ({ default: m.ProfileLayout })))
export const IdentityProfile = lazy(() => identityRemote().then(m => ({ default: m.Profile })))
export const IdentitySearch = lazy(() => identityRemote().then(m => ({ default: m.Search })))
export const IdentityVerifyEmail = lazy(() => identityRemote().then(m => ({ default: m.VerifyEmail })))
export const IdentityEmailChangeUndo = lazy(() => identityRemote().then(m => ({ default: m.EmailChangeUndo })))
export const IdentityForgotPassword = lazy(() => identityRemote().then(m => ({ default: m.ForgotPassword })))
export const IdentityResetPassword = lazy(() => identityRemote().then(m => ({ default: m.ResetPassword })))
export const IdentityOAuthCallback = lazy(() => identityRemote().then(m => ({ default: m.OAuthCallback })))
export const IdentityPlayerStats = lazy(() => identityRemote().then(m => ({ default: m.PlayerStats })))
export const IdentityMatchScorecard = lazy(() => identityRemote().then(m => ({ default: m.MatchScorecardView })))

// Signing up for one competition (team or free agent), owned by the org app and
// shared with a league's competition pages — so a player can enter from
// /compete without being sent to the league's site first.
const orgRegistration = withReload(() => import('../../org/src/registration.tsx'))

export const OrgCompetitionRegistration = lazy(() => orgRegistration().then(m => ({ default: m.CompetitionRegistration })))
